import {
  api,
  type GetCustomerAccountApplicationApiArg,
  type GetLedgerEntriesApiArg,
  type GetLedgerEntriesApiResponse,
} from "@generated/redux/services/generated/api";

/**
 * Recipient caches (the combined list and the single fiat/blockchain
 * recipient) stay in the store for this long after their last subscriber
 * unmounts, so a revisit paints cached rows instead of a skeleton.
 */
export const RECIPIENT_CACHE_SECONDS = 300;

/**
 * Pass as `refetchOnMountOrArgChange` at EVERY recipient query call site.
 * Up to this age a remount serves the cache with no request (the old 60s
 * default behaviour); past it, and up to `RECIPIENT_CACHE_SECONDS`, the
 * cached rows render while a background refetch runs. A call site without it
 * would serve trust/active state up to `RECIPIENT_CACHE_SECONDS` old.
 */
export const RECIPIENT_REFETCH_AFTER_SECONDS = 60;

export const enhancedApi = api
  .enhanceEndpoints({
    // "PaymentLists" and "Beneficiaries" split the generated catch-all "core"
    // tag (provided by the recipients list AND both payment lists), so a
    // payment action no longer evicts the recipients list and a recipient
    // edit no longer refetches the payment lists. The three providers keep
    // "core" as well, as a safety net should a generated mutation ever start
    // invalidating it; nothing hand-written invalidates "core" any more.
    addTagTypes: [
      "LedgerEntries",
      "CustomerAccountBalances",
      "PaymentLists",
      "Beneficiaries",
    ],
    endpoints: {
      getUnconfirmedPayments: {
        providesTags: ["core", "PaymentLists"],
      },
      getProcessingPayments: {
        providesTags: ["core", "PaymentLists"],
      },
      getAllBeneficiaries: {
        keepUnusedDataFor: RECIPIENT_CACHE_SECONDS,
        providesTags: ["core", "Beneficiaries"],
      },
      getBeneficiary: {
        keepUnusedDataFor: RECIPIENT_CACHE_SECONDS,
      },
      getBlockchainBeneficiary: {
        keepUnusedDataFor: RECIPIENT_CACHE_SECONDS,
      },
      // Static reference data: templates per currency/country/type, payment
      // reasons per currency/country, and the VASP directory.
      getBeneficiaryTemplates: {
        keepUnusedDataFor: 600,
      },
      getPaymentReasons: {
        keepUnusedDataFor: 600,
      },
      getVasps: {
        keepUnusedDataFor: 600,
      },
      getPricingRequest: {
        keepUnusedDataFor: 300,
        providesTags: (_r, _e, arg) => [
          { type: "pricing-engine", id: arg.uuid },
        ],
      },
      getProducts: {
        keepUnusedDataFor: 600,
      },
      getCountries: {
        keepUnusedDataFor: 1800,
      },
      getVirtualAssets: {
        keepUnusedDataFor: 600,
      },
      // createPricingRequest, requotePricingRequest and acceptPricingRequest
      // all return the full FilteredPricingEngineRequest — seed the
      // getPricingRequest cache directly from the mutation response instead
      // of invalidating tags (which would trigger a redundant GET for data
      // we already have).
      createPricingRequest: {
        invalidatesTags: [],
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data?.id) {
              // upsertQueryData (not updateQueryData) because the cache entry
              // doesn't exist yet on first CREATE — updateQueryData would no-op.
              dispatch(
                api.util.upsertQueryData(
                  "getPricingRequest",
                  {
                    uuid: data.id,
                  },
                  data
                )
              );
            }
          } catch {
            // If the mutation fails, the cache is not updated
          }
        },
      },
      // A requote re-prices the request in place — it never changes
      // downstream payment lists, so seeding is all it needs.
      requotePricingRequest: {
        invalidatesTags: [],
        onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data?.id) {
              dispatch(
                api.util.updateQueryData(
                  "getPricingRequest",
                  { uuid },
                  () => data
                )
              );
            }
          } catch {
            // If the mutation fails, the cache is not updated
          }
        },
      },
      // Accepting books the exchange upstream: it creates the payment /
      // withdrawal resource, moves balances, and writes ledger entries — so
      // on top of the same seeding it invalidates the downstream caches,
      // matching the confirm/cancel set below
      // (future-cache-invalidation-improvements.md §3 — splitting the old
      // generic executeOperation endpoint by purpose is what made this
      // expressible without penalising requotes). Today the refetch work is
      // carried by "PaymentLists" (the unconfirmed/processing lists),
      // "customer-accounts" (balances, capabilities — every money movement
      // must refetch balances, so never drop it) and "LedgerEntries";
      // "payments"/"blockchain-withdrawals" have no providers yet and are
      // kept for parity with confirm/cancel so all five mutations light up
      // together if those tags ever gain them. The recipients list
      // ("Beneficiaries") carries no payment-derived field, so a payment
      // leaves it cached.
      acceptPricingRequest: {
        invalidatesTags: [
          "payments",
          "blockchain-withdrawals",
          "LedgerEntries",
          "customer-accounts",
          "PaymentLists",
        ],
        onQueryStarted: async ({ uuid }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data?.id) {
              dispatch(
                api.util.updateQueryData(
                  "getPricingRequest",
                  { uuid },
                  () => data
                )
              );
            }
          } catch {
            // If the mutation fails, the cache is not updated
          }
        },
      },
      // Confirm / cancel mutations both invalidate the same downstream caches
      // because both transitions move a payment between Pending, Processing,
      // and settled/cancelled states. "ledger" is intentionally omitted — the
      // getLedgerEntries override below replaces the generated provider with
      // "LedgerEntries", so "ledger" has no live subscribers in this app.
      // "customer-accounts" stays so balances refetch after every money
      // movement; "PaymentLists" refreshes the two payment lists without
      // touching the recipients list.
      confirmPaymentInstruction: {
        invalidatesTags: [
          "payments",
          "LedgerEntries",
          "customer-accounts",
          "PaymentLists",
        ],
      },
      confirmBlockchainWithdrawalInstruction: {
        invalidatesTags: [
          "blockchain-withdrawals",
          "LedgerEntries",
          "customer-accounts",
          "PaymentLists",
        ],
      },
      cancelPaymentInstruction: {
        invalidatesTags: [
          "payments",
          "LedgerEntries",
          "customer-accounts",
          "PaymentLists",
        ],
      },
      cancelBlockchainWithdrawalInstruction: {
        invalidatesTags: [
          "blockchain-withdrawals",
          "LedgerEntries",
          "customer-accounts",
          "PaymentLists",
        ],
      },
      // Beneficiary mutations also invalidate "Beneficiaries" so the combined
      // getAllBeneficiaries list refreshes; the payment lists are untouched.
      // "account" / "journal-blockchain" refresh the single-recipient queries.
      createBeneficiary: {
        invalidatesTags: ["account", "Beneficiaries"],
      },
      updateBeneficiary: {
        invalidatesTags: ["account", "Beneficiaries"],
      },
      deleteBeneficiary: {
        invalidatesTags: ["account", "Beneficiaries"],
      },
      createBlockchainBeneficiary: {
        invalidatesTags: ["journal-blockchain", "Beneficiaries"],
      },
      updateBlockchainBeneficiary: {
        invalidatesTags: ["journal-blockchain", "Beneficiaries"],
      },
      deleteBlockchainBeneficiary: {
        invalidatesTags: ["journal-blockchain", "Beneficiaries"],
      },
      // Use the PATCH response to update the cache directly instead of
      // invalidating (which triggers a redundant GET on the next page).
      // Preserve the sent formData as the cache source of truth —
      // useOnboardingStep's merge already builds the complete formData.
      updateCustomerAccountApplication: {
        invalidatesTags: [],
        onQueryStarted: async (
          { applicationId, updateCustomerAccountApplicationRequest },
          { dispatch, queryFulfilled }
        ) => {
          try {
            const { data: updatedApp } = await queryFulfilled;
            dispatch(
              api.util.updateQueryData(
                "getCustomerAccountApplication",
                {
                  applicationId,
                } as GetCustomerAccountApplicationApiArg,
                () => ({
                  ...updatedApp,
                  formData:
                    updateCustomerAccountApplicationRequest?.formData ??
                    updatedApp.formData,
                })
              )
            );
          } catch {
            // If the mutation fails, the cache is not updated
          }
        },
      },
    },
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      // Every page lives in one cache entry (`pageCursor` is dropped from the
      // key) and `merge` appends whatever a cursor request returns, so a
      // `LedgerEntries` invalidation that reaches a mounted list whose cursor
      // points at page N appends page N again.
      // See .claude/rules/web/rtk-query.md → "getLedgerEntries".
      // Who refreshes this entry today, and why none re-appends a page:
      // - Ledger header refresh button: calls `useLedgerEntryList().refresh()`,
      //   and ignores presses while `isFetching` (`aria-disabled` plus an early
      //   return in its handler; `aria-disabled` alone blocks no activation).
      // - `LedgerEntryListProvider` `refetch` / `refresh`: no tag invalidation;
      //   they reset the cursor, then refetch the page-1 args directly once
      //   the reset has rendered.
      // - Dashboard mount and tab change, `hooks/useDashboardMountRefresh.ts`
      //   (used by `app/**/(customerAccount)/page.tsx`; skips the first mount
      //   right after a boot prefetch): `LedgerEntries` with `PaymentLists` on
      //   mount, `LedgerEntries` on a
      //   switch to activity): not believed to reproduce, because the list
      //   mounts fresh at page-1 args.
      // - `acceptPricingRequest` (above): runs only in `PricingProvider`, under
      //   the exchange and pay flow layouts, where no ledger is mounted.
      // - The four confirm/cancel payment and blockchain-withdrawal
      //   instructions (above): the activity, processing and pending tabs are
      //   mutually exclusive, so the ledger is unmounted when they resolve.
      // - `useRefreshAllLists().refreshAllLists` (the pending list's confirm
      //   callback): the same tab exclusivity, on the dashboard and the
      //   account page.
      // - `useOtpPaymentFlow` completion: runs in the pay flow, where no
      //   ledger is mounted.
      // - `LedgerEntryListProvider` customer-account switch: only PARTLY
      //   covered. The cursor and filters reset during the render that sees
      //   the new account id, so the new account's first request is page 1.
      //   But the switch effect still dispatches a `LedgerEntries`
      //   invalidation while the list is mounted; RTK Query holds it until no
      //   request in the api is pending, and if the new account's list has
      //   paged on by then, it refetches that page and appends it again (the
      //   residual gap in the rule).
      getLedgerEntries: builder.query<
        GetLedgerEntriesApiResponse,
        GetLedgerEntriesApiArg
      >({
        query: (params) => ({
          url: "/ledger-entries",
          params,
        }),
        serializeQueryArgs: ({ queryArgs, endpointName }) => {
          const { pageCursor: _, ...filteredArgs } = queryArgs;
          return `${endpointName}(${JSON.stringify(filteredArgs)})`;
        },
        merge: (currentCache, newItems, { arg }) => {
          if (!arg.pageCursor) {
            return newItems;
          }
          return {
            ...newItems,
            data: [...(currentCache.data || []), ...(newItems.data || [])],
          };
        },
        forceRefetch({ currentArg, previousArg }) {
          return currentArg?.pageCursor !== previousArg?.pageCursor;
        },
        providesTags: ["LedgerEntries"],
      }),

      getDebugSentry: builder.query<{ message: string }, void>({
        query: () => "/debug-sentry",
      }),
    }),
  });

export const {
  useGetLedgerEntriesQuery,
  useLazyGetLedgerEntriesQuery,
  useLazyGetDebugSentryQuery,
} = enhancedApi;
